import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import { useState } from 'react';
import {
  ALLOWED_DECIMAL_PLACES_FOR_DIMENSION,
  useBlockDimensionChangedHandler,
  useBlockDimensionChangedHandlerWithoutUndoRedo,
} from 'components/editor/hooks/UseBlockDimensionChangedHandler';
import { getRoundedValue } from 'components/editor/GridDndEditor/gridHelper.tsx';

export const DIMENSION_WIDTH = 'width';
export const DIMENSION_HEIGHT = 'height';

export type DimensionType = typeof DIMENSION_WIDTH | typeof DIMENSION_HEIGHT;

export function useAspectRatio({
  imageAspectRatioLock,
  blockContent,
  sectionId,
  blockId,
}: {
  imageAspectRatioLock: boolean;
  blockContent: BlockContent | undefined;
  sectionId: string | null;
  blockId: string | null;
}) {
  const { width, height } = blockContent?.blockConfig || { width: 0, height: 0 };
  const [originalAspectRatio] = useState<number | null>(width / height);
  const blockDimensionChangedHandler = useBlockDimensionChangedHandler();
  const blockDimensionChangedHandlerWithoutUndoRedo = useBlockDimensionChangedHandlerWithoutUndoRedo();

  const handleImageDimensionChange = (
    changedDimension: DimensionType,
    updatedDimensionValue: number,
    commitToHistory = true,
    originalDimensions?: { width: number; height: number }
  ) => {
    if (sectionId && blockId) {
      let newWidth = width;
      let newHeight = height;
      const isWidthChanged = changedDimension === DIMENSION_WIDTH;

      if (imageAspectRatioLock && originalAspectRatio) {
        if (isWidthChanged) {
          newWidth = updatedDimensionValue;
          newHeight = updatedDimensionValue / originalAspectRatio;
        } else {
          newHeight = updatedDimensionValue;
          newWidth = updatedDimensionValue * originalAspectRatio;
        }
      } else {
        if (isWidthChanged) {
          newWidth = updatedDimensionValue;
        } else {
          newHeight = updatedDimensionValue;
        }
      }

      newWidth = getRoundedValue(newWidth, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION);
      newHeight = getRoundedValue(newHeight, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION);

      const stateBeforeResizing = {
        widthPx: originalDimensions ? originalDimensions.width : width,
        heightPx: originalDimensions ? originalDimensions.height : height,
        xAxisPx: blockContent?.blockConfig.x || 0,
        yAxisPx: blockContent?.blockConfig.y || 0,
      };

      const stateAfterResizing = {
        widthPx: newWidth,
        heightPx: newHeight,
        xAxisPx: blockContent?.blockConfig.x || 0,
        yAxisPx: blockContent?.blockConfig.y || 0,
      };

      if (sectionId && blockId) {
        if (commitToHistory) {
          blockDimensionChangedHandler(sectionId, blockId, stateBeforeResizing, stateAfterResizing);
        } else {
          blockDimensionChangedHandlerWithoutUndoRedo(sectionId, blockId, { widthPx: newWidth, heightPx: newHeight });
        }
      }
    }
  };

  return { originalAspectRatio, handleImageDimensionChange };
}
